import { Box, Button, Chip, Grid, Typography } from "@mui/material";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext, SubscriptionTab } from "contexts/Subscription";
import { useContext } from "react";
import { SubscriptionType } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useSubscription } from "utils/useSubscription";

import {
  PlanAction,
  getCurrentPlanButton,
  getPlanLimitText,
  isCurrentPlan,
} from "../helpers";
import FollowUpAssistantItem from "./FollowUpAssistantItem";
import styles from "./styles";

interface Props {
  plan: Plans;
}

const ProPlanCard = ({ plan }: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { plans, emailSents, subscription, tab } =
    useContext(SubscriptionContext);
  const { onPlanButtonClick } = useSubscription();

  const planName = plans?.[plan]?.display_name;
  const totalEmails = plans?.[plan]?.monthly_limit || 0;
  const action = getCurrentPlanButton(
    plan,
    subscription?.subscription?.planName || Plans.FREE,
    subscription,
  );

  const BENEFITS = [
    {
      icon: "🚀",
      primary: (
        <>
          Send <strong>{totalEmails} emails</strong> per month
        </>
      ),
    },
    {
      icon: "🧠",
      primary: (
        <>
          Get <strong>Advanced Brand Filters</strong> (e.g "Brands that work
          with UGC creators") to refine your search
        </>
      ),
    },
    {
      icon: "📈",
      primary: (
        <>
          <strong> See reply likelihood scores</strong> for each contact
        </>
      ),
    },
    {
      icon: "🔑",
      primary: (
        <>
          Get access to <strong>600k+ Brand Contacts</strong>
        </>
      ),
    },
  ];

  const _isCurrentPlan =
    isCurrentPlan(subscription, plan) &&
    (subscription?.subscription?.status === SubscriptionType.ACTIVE ||
      subscription?.subscription?.status === SubscriptionType.PAST_DUE);

  return (
    <>
      <Box sx={styles.card}>
        <Box sx={[styles.planTitleCard, { backgroundColor: "primary.main" }]}>
          <Grid container justifyContent="flex-end">
            <Chip
              sx={{ backgroundColor: "yellow.main", px: 1 }}
              label={
                <Typography variant="subtitle2" sx={{ fontSize: 13 }}>
                  <strong>Great for creators who pitch often</strong>
                </Typography>
              }
            ></Chip>
          </Grid>

          <Typography variant="h3" sx={{ mt: 6, color: "white" }}>
            {planName}{" "}
            {_isCurrentPlan && (
              <Box component="span" sx={{ color: "yellow.main", fontSize: 16 }}>
                (Current Plan)
              </Box>
            )}
          </Typography>

          <Box sx={styles.price}>
            {tab === SubscriptionTab.yearly && (
              <>
                <Box sx={styles.strike}>
                  <Box>${plans && plans[Plans.PRO]?.price}/mo</Box>
                </Box>{" "}
                <Box>${plans && plans[Plans.PRO_YEARLY]?.price}/mo</Box>
              </>
            )}
            {tab === SubscriptionTab.monthly && (
              <Box>${plans && plans[Plans.PRO]?.price}/mo</Box>
            )}
          </Box>
        </Box>

        {BENEFITS.map((benefit, index) => (
          <Grid container key={index} alignItems="center" sx={{ mb: 1.5 }}>
            <Grid
              container
              justifyContent="left"
              alignItems="center"
              item
              xs="auto"
            >
              <Box sx={styles.iconBlock}>{benefit.icon}</Box>
            </Grid>
            <Grid item xs md>
              <Typography component="div">{benefit.primary}</Typography>
            </Grid>
          </Grid>
        ))}
        <FollowUpAssistantItem
          isCurrentPlan={_isCurrentPlan}
          subscription={subscription}
        />

        {!currentOrg?.overridePlanName && (
          <Button
            disableElevation
            fullWidth
            variant={action === PlanAction.upgrade ? "contained" : "text"}
            sx={{
              mt: 3,
              py: 1,
              borderRadius: 2,
              backgroundColor:
                action === PlanAction.upgrade ? "primary.main" : "#f4f8f6",
            }}
            onClick={() => onPlanButtonClick(plan, action)}
          >
            {action}{" "}
            {tab === SubscriptionTab.yearly &&
            (action === PlanAction.upgrade || action === PlanAction.downgrade)
              ? "+ Get Free Template"
              : ""}
          </Button>
        )}
      </Box>

      <Box sx={{ mt: 2 }}>
        {getPlanLimitText(subscription, plan, emailSents)}
      </Box>
    </>
  );
};

export default ProPlanCard;
